import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Key, Copy, Download, Camera, Upload, CheckCircle2, AlertTriangle, User, X, Image as ImageIcon } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { Html5Qrcode } from 'html5-qrcode';
import { secretKeyService } from '../../services/secretKeyService';
import { collectionService } from '../../services/collectionService';
import { CollectionItem } from '../../types';
import { useToast } from '../../context/ToastContext';

export const SecretKeyManager: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();

  const [userCode, setUserCode] = useState<string>('');
  const [copied, setCopied] = useState<boolean>(false);

  // Author Name Management state
  const [authorInput, setAuthorInput] = useState<string>(() => secretKeyService.getUserAuthorName());
  const [updatingAuthor, setUpdatingAuthor] = useState<boolean>(false);

  // Restore Mode: 'text' | 'camera' | 'upload'
  const [restoreMode, setRestoreMode] = useState<'text' | 'camera' | 'upload'>('text');
  const [inputText, setInputText] = useState<string>('');

  // Camera QR scanner ref
  const html5QrCodeRef = useRef<Html5Qrcode | null>(null);

  // Upload QR image file state
  const [isScanningFile, setIsScanningFile] = useState<boolean>(false);

  // Owned collections found
  const [ownedCollections, setOwnedCollections] = useState<CollectionItem[]>([]);
  const [loadingOwned, setLoadingOwned] = useState<boolean>(false);

  useEffect(() => {
    loadUserCode();
  }, []);

  const loadUserCode = async () => {
    const code = secretKeyService.getUserRecoveryCode();
    setUserCode(code);
    await checkOwnedCollections(code);
  };

  const checkOwnedCollections = async (code: string) => {
    setLoadingOwned(true);
    try {
      const userHash = await secretKeyService.hashKey(code);
      const res = await collectionService.fetchCollections(1, 100);
      const ownedMatches: CollectionItem[] = [];
      for (const item of res.data) {
        if (item.secretKeyHash) {
          if (item.secretKeyHash === userHash) {
            ownedMatches.push(item);
          }
        } else {
          const explicitKey = secretKeyService.getExplicitKeyForCollection(item.id);
          if (explicitKey === code) {
            ownedMatches.push(item);
          }
        }
      }
      setOwnedCollections(ownedMatches);

      // Auto pre-fill author name if user already entered it in any owned collection
      const currentAuthor = secretKeyService.getUserAuthorName();
      if (!currentAuthor) {
        const foundAuthor = ownedMatches.find((item) => item.author && !item.author.startsWith('User-'))?.author;
        if (foundAuthor) {
          secretKeyService.setUserAuthorName(foundAuthor);
          setAuthorInput(foundAuthor);
        }
      } else {
        setAuthorInput(currentAuthor);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingOwned(false);
    }
  };

  // Cleanup camera scanner on unmount or tab switch
  useEffect(() => {
    return () => {
      stopCameraScanner();
    };
  }, []);

  const stopCameraScanner = async () => {
    if (html5QrCodeRef.current) {
      try {
        if (html5QrCodeRef.current.isScanning) {
          await html5QrCodeRef.current.stop();
        }
        html5QrCodeRef.current.clear();
      } catch (e) {
        console.error('Lỗi khi dừng camera QR scanner', e);
      }
      html5QrCodeRef.current = null;
    }
  };

  const handleCopyCode = () => {
    if (!userCode) return;
    navigator.clipboard.writeText(userCode);
    setCopied(true);
    toast.success('Đã sao chép Mã Khôi Phục Cá Nhân!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadQR = () => {
    const svg = document.getElementById('user-qr-code-svg');
    if (!svg) {
      toast.error('Không tìm thấy hình QR code.');
      return;
    }

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      canvas.width = img.width + 40;
      canvas.height = img.height + 40;
      if (ctx) {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 20, 20);

        const pngUrl = canvas.toDataURL('image/png');
        const downloadLink = document.createElement('a');
        downloadLink.href = pngUrl;
        downloadLink.download = `qt_user_recovery_qr_${userCode.slice(-6)}.png`;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        toast.success('Đã tải ảnh QR khôi phục về máy thành công!');
      }
    };

    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  const handleUpdateAuthorName = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanAuthor = authorInput.trim();
    if (!cleanAuthor) {
      toast.warning('Vui lòng nhập Tên Tác Giả.');
      return;
    }

    setUpdatingAuthor(true);
    try {
      secretKeyService.setUserAuthorName(cleanAuthor);
      const userHash = await secretKeyService.hashKey(userCode);
      const res = await collectionService.updateAuthorName({
        secretKeyHash: userHash,
        author: cleanAuthor,
      });

      toast.success(`Đã đổi tên tác giả thành "${cleanAuthor}" và áp dụng cho ${res.updatedCount} Bộ Sưu Tập!`);
      await checkOwnedCollections(userCode);
    } catch (err: any) {
      toast.error(err.message || 'Lỗi khi cập nhật tên tác giả.');
    } finally {
      setUpdatingAuthor(false);
    }
  };

  const applyRestoredCode = async (code: string) => {
    const cleanCode = code.trim();
    if (!secretKeyService.isValidUUID(cleanCode)) {
      toast.error('Mã khôi phục không đúng định dạng UUID v4 hợp lệ.');
      return;
    }

    try {
      secretKeyService.setUserRecoveryCode(cleanCode);
      setUserCode(cleanCode);
      toast.success('Đã khôi phục thành công Mã Khôi Phục Cá Nhân!');
      await stopCameraScanner();
      setInputText('');
      await checkOwnedCollections(cleanCode);
    } catch (err: any) {
      toast.error(err.message || 'Lỗi khi khôi phục mã.');
    }
  };

  const handleTextRestore = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputText.trim()) {
      toast.warning('Vui lòng nhập mã khôi phục.');
      return;
    }
    await applyRestoredCode(inputText);
  };

  const startCameraScanner = async () => {
    await stopCameraScanner();
    setRestoreMode('camera');

    setTimeout(async () => {
      try {
        const scanner = new Html5Qrcode('qr-camera-reader');
        html5QrCodeRef.current = scanner;

        await scanner.start(
          { facingMode: 'environment' },
          {
            fps: 15,
            qrbox: (viewfinderWidth, viewfinderHeight) => {
              const minDim = Math.min(viewfinderWidth, viewfinderHeight);
              return {
                width: Math.floor(minDim * 0.75),
                height: Math.floor(minDim * 0.75),
              };
            },
          },
          async (decodedText) => {
            await stopCameraScanner();
            setRestoreMode('text');
            await applyRestoredCode(decodedText);
          },
          () => {
            // ignore scan errors per frame
          }
        );
      } catch (err: any) {
        console.error('Không thể mở camera:', err);
        toast.error('Không thể mở camera. Vui lòng kiểm tra quyền truy cập camera của trình duyệt.');
      }
    }, 100);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsScanningFile(true);

    try {
      const html5QrCode = new Html5Qrcode('qr-file-dummy-reader');
      try {
        const decodedText = await html5QrCode.scanFile(file, true);
        if (decodedText) {
          await applyRestoredCode(decodedText);
        } else {
          toast.error('Không tìm thấy mã QR hợp lệ trong hình ảnh đã chọn.');
        }
      } finally {
        try {
          html5QrCode.clear();
        } catch (e) {
          // Ignore cleanup errors
        }
      }
    } catch (err: any) {
      console.error(err);
      toast.error('Không thể đọc mã QR từ file ảnh này. Vui lòng chọn ảnh QR rõ nét hơn.');
    } finally {
      setIsScanningFile(false);
      e.target.value = '';
    }
  };

  return (
    <div style={{ maxWidth: '760px', margin: '0 auto', paddingBottom: '2.5rem' }}>
      {/* Back button */}
      <button
        onClick={() => navigate('/collections')}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.375rem',
          padding: '0.5rem 0.75rem',
          borderRadius: '0.375rem',
          backgroundColor: '#ffffff',
          border: '1px solid var(--border-light)',
          color: 'var(--text-on-light)',
          fontWeight: 500,
          fontSize: '0.875rem',
          cursor: 'pointer',
          marginBottom: '1.25rem',
        }}
      >
        <ArrowLeft size={16} /> Quay Lại Chợ Bộ Sưu Tập
      </button>

      {/* Main Account Recovery Code Card */}
      <div
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '0.75rem',
          border: '1px solid var(--border-light)',
          padding: '1.5rem',
          marginBottom: '1.5rem',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.03)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', marginBottom: '0.5rem' }}>
          <div
            style={{
              padding: '0.4rem',
              borderRadius: '0.5rem',
              backgroundColor: 'var(--accent-highlight)',
              color: 'var(--text-on-light)',
              display: 'flex',
              flexShrink: 0,
            }}
          >
            <Key size={20} />
          </div>
          <div>
            <h2 style={{ fontSize: '1.0625rem', fontWeight: 700, margin: 0, color: 'var(--text-main)' }}>
              Mã Khôi Phục Cá Nhân
            </h2>
            <p style={{ fontSize: '0.75rem', color: '#666', margin: '0.125rem 0 0 0' }}>
              Dùng 1 chìa khóa duy nhất để sở hữu & quản lý toàn bộ bộ sưu tập.
            </p>
          </div>
        </div>

        {/* UUID Display & Action Bar */}
        <div style={{ marginTop: '1.25rem', marginBottom: '1.25rem' }}>
          <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: '#64748b', marginBottom: '0.375rem' }}>
            Mã Khôi Phục Của Thiết Bị Này:
          </label>

          {/* Sleek UUID Pill (No double nested border) */}
          <div
            style={{
              width: '100%',
              backgroundColor: '#f0f9ff',
              border: '1px solid #bae6fd',
              borderRadius: '0.5rem',
              padding: '0.75rem 1rem',
              textAlign: 'center',
              boxSizing: 'border-box',
              marginBottom: '0.75rem',
            }}
          >
            <code
              style={{
                display: 'block',
                fontSize: '0.9375rem',
                fontWeight: 700,
                color: '#0284c7',
                fontFamily: 'monospace',
                whiteSpace: 'nowrap',
                overflowX: 'auto',
                letterSpacing: '0.03em',
              }}
            >
              {userCode}
            </code>
          </div>

          {/* Action Buttons Row */}
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={handleCopyCode}
              style={{
                flex: 1,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.375rem',
                padding: '0.625rem 0.75rem',
                borderRadius: '0.375rem',
                backgroundColor: copied ? '#dcfce7' : '#ffffff',
                border: '1px solid var(--border-light)',
                color: copied ? '#15803d' : 'var(--text-on-light)',
                fontWeight: 700,
                fontSize: '0.8125rem',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              {copied ? <CheckCircle2 size={16} /> : <Copy size={16} />}
              <span className="text-desktop">{copied ? 'Đã sao chép mã' : 'Sao chép mã'}</span>
              <span className="text-mobile">{copied ? 'Đã copy' : 'Sao chép mã'}</span>
            </button>

            <button
              onClick={handleDownloadQR}
              style={{
                flex: 1,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.375rem',
                padding: '0.625rem 0.75rem',
                borderRadius: '0.375rem',
                backgroundColor: 'var(--primary-action)',
                border: 'none',
                color: '#0f172a',
                fontWeight: 700,
                fontSize: '0.8125rem',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              <Download size={16} />
              <span className="text-desktop">Tải ảnh QR</span>
              <span className="text-mobile">Tải ảnh QR</span>
            </button>
          </div>

          {/* QR Code Showcase */}
          <div style={{ marginTop: '1.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{ padding: '0.5rem', display: 'inline-block' }}>
              {userCode && (
                <QRCodeSVG
                  id="user-qr-code-svg"
                  value={userCode}
                  size={160}
                  level="H"
                  includeMargin={true}
                />
              )}
            </div>
            <p style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.375rem', textAlign: 'center' }}>
              Quét hình QR trên để khôi phục nhanh trên thiết bị khác
            </p>
          </div>
        </div>

        {/* Warning Banner (Soft alert without harsh box border) */}
        <div
          style={{
            backgroundColor: '#fffbe6',
            borderRadius: '0.5rem',
            padding: '0.75rem 1rem',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '0.625rem',
            fontSize: '0.8125rem',
            color: '#856404',
          }}
        >
          <AlertTriangle size={18} style={{ color: '#d97706', flexShrink: 0, marginTop: '0.125rem' }} />
          <div>
            <strong>CẢNH BÁO QUAN TRỌNG:</strong> Đây là chìa khóa duy nhất đại diện cho tài khoản no-login của bạn. Hãy lưu ảnh QR hoặc dán mã vào nơi an toàn. Mất mã này = mất quyền quản trị tất cả các Bộ Sưu Tập vĩnh viễn!
          </div>
        </div>
      </div>

      {/* Author Profile Card */}
      <div
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '0.75rem',
          border: '1px solid var(--border-light)',
          padding: '1.25rem',
          marginBottom: '1.25rem',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.03)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', marginBottom: '0.625rem' }}>
          <div
            style={{
              padding: '0.4rem',
              borderRadius: '0.5rem',
              backgroundColor: '#e0f2fe',
              color: '#0284c7',
              display: 'flex',
              flexShrink: 0,
            }}
          >
            <User size={20} />
          </div>
          <div>
            <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: 0, color: 'var(--text-main)' }}>
              Thông Tin Tác Giả
            </h3>
            <p style={{ fontSize: '0.75rem', color: '#666', margin: '0.125rem 0 0 0' }}>
              Tên này tự động mặc định khi tạo mới và đồng bộ tất cả bộ sưu tập cũ.
            </p>
          </div>
        </div>

        <form onSubmit={handleUpdateAuthorName} style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
          <input
            type="text"
            placeholder="Nhập biệt danh / tên tác giả..."
            value={authorInput}
            onChange={(e) => setAuthorInput(e.target.value)}
            maxLength={50}
            style={{
              flex: 1,
              minWidth: 0,
              padding: '0.5rem 0.75rem',
              borderRadius: '0.375rem',
              border: '1px solid var(--border-light)',
              fontSize: '0.875rem',
              backgroundColor: '#ffffff',
            }}
          />
          <button
            type="submit"
            disabled={updatingAuthor}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.375rem',
              padding: '0.5rem 0.875rem',
              borderRadius: '0.375rem',
              backgroundColor: 'var(--primary-action)',
              color: '#0f172a',
              border: 'none',
              fontWeight: 700,
              fontSize: '0.875rem',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              cursor: updatingAuthor ? 'wait' : 'pointer',
            }}
          >
            {updatingAuthor ? (
              <span>Đang lưu...</span>
            ) : (
              <>
                <User size={16} />
                <span className="text-desktop">Cập Nhật Cho Tất Cả Bộ Sưu Tập</span>
                <span className="text-mobile">Cập Nhật</span>
              </>
            )}
          </button>
        </form>
      </div>

      {/* Restore Section */}
      <div
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '0.75rem',
          border: '1px solid var(--border-light)',
          padding: '1.25rem',
          marginBottom: '1.5rem',
        }}
      >
        <h3 style={{ fontSize: '1.0625rem', fontWeight: 700, margin: '0 0 0.875rem 0', color: 'var(--text-main)' }}>
          Khôi Phục Mã Cá Nhân Trên Thiết Bị Mới
        </h3>

        {/* Navigation Tabs for Restore Mode */}
        <div style={{ display: 'flex', gap: '0.375rem', marginBottom: '1.25rem' }}>
          <button
            onClick={() => {
              stopCameraScanner();
              setRestoreMode('text');
            }}
            style={{
              flex: 1,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.25rem',
              padding: '0.5rem 0.5rem',
              borderRadius: '0.375rem',
              backgroundColor: restoreMode === 'text' ? 'var(--primary-action)' : '#f1f5f9',
              color: restoreMode === 'text' ? '#0f172a' : '#475569',
              border: 'none',
              fontWeight: 600,
              fontSize: '0.8125rem',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            <Key size={14} />
            <span className="text-desktop">Dán Chuỗi Text</span>
            <span className="text-mobile">Dán Text</span>
          </button>
          <button
            onClick={startCameraScanner}
            style={{
              flex: 1,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.25rem',
              padding: '0.5rem 0.5rem',
              borderRadius: '0.375rem',
              backgroundColor: restoreMode === 'camera' ? 'var(--primary-action)' : '#f1f5f9',
              color: restoreMode === 'camera' ? '#0f172a' : '#475569',
              border: 'none',
              fontWeight: 600,
              fontSize: '0.8125rem',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            <Camera size={14} />
            <span className="text-desktop">Quét Camera QR</span>
            <span className="text-mobile">Quét QR</span>
          </button>
          <button
            onClick={() => {
              stopCameraScanner();
              setRestoreMode('upload');
            }}
            style={{
              flex: 1,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.25rem',
              padding: '0.5rem 0.5rem',
              borderRadius: '0.375rem',
              backgroundColor: restoreMode === 'upload' ? 'var(--primary-action)' : '#f1f5f9',
              color: restoreMode === 'upload' ? '#0f172a' : '#475569',
              border: 'none',
              fontWeight: 600,
              fontSize: '0.8125rem',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            <Upload size={14} />
            <span className="text-desktop">Tải Ảnh QR Lên</span>
            <span className="text-mobile">Tải QR</span>
          </button>
        </div>

        {/* Mode 1: Text Input */}
        {restoreMode === 'text' && (
          <form onSubmit={handleTextRestore}>
            <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 600, color: '#333', marginBottom: '0.375rem' }}>
              Dán chuỗi UUID 36 ký tự:
            </label>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <input
                type="text"
                placeholder="VD: 550e8400-e29b-41d4-a716-446655440000"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                style={{
                  flex: 1,
                  minWidth: '240px',
                  padding: '0.5rem 0.75rem',
                  borderRadius: '0.375rem',
                  border: '1px solid var(--border-light)',
                  fontSize: '0.875rem',
                }}
              />
              <button
                type="submit"
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '0.375rem',
                  backgroundColor: 'var(--primary-action)',
                  color: '#0f172a',
                  border: 'none',
                  fontWeight: 700,
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                }}
              >
                Xác Nhận Khôi Phục
              </button>
            </div>
          </form>
        )}

        {/* Mode 2: Google Lens Fullscreen Camera Scanner Overlay */}
        {restoreMode === 'camera' && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              backgroundColor: '#000000',
              zIndex: 999999,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              boxSizing: 'border-box',
              overflow: 'hidden',
            }}
          >
            {/* Full-width Edge-to-Edge Camera Stream Container */}
            <div
              id="qr-camera-reader"
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                zIndex: 1,
              }}
            />

            {/* Top Google Lens Gradient Header */}
            <div
              style={{
                position: 'relative',
                zIndex: 50,
                width: '100%',
                padding: '1rem 1.25rem',
                background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.85) 0%, rgba(0, 0, 0, 0) 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                boxSizing: 'border-box',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <button
                  onClick={() => {
                    stopCameraScanner();
                    setRestoreMode('text');
                  }}
                  style={{
                    padding: '0.375rem',
                    borderRadius: '9999px',
                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                    color: '#ffffff',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <ArrowLeft size={20} />
                </button>
                <span style={{ fontSize: '1.125rem', fontWeight: 700, color: '#ffffff', letterSpacing: '0.01em' }}>
                  Quét Mã QR Khôi Phục
                </span>
              </div>

              <button
                onClick={() => {
                  stopCameraScanner();
                  setRestoreMode('text');
                }}
                style={{
                  padding: '0.5rem',
                  borderRadius: '9999px',
                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                  color: '#ffffff',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                title="Đóng Camera"
              >
                <X size={20} />
              </button>
            </div>

            {/* Center Google Lens Rounded Viewfinder Frame (Overlaid on camera video) */}
            <div
              style={{
                position: 'absolute',
                top: '46%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '280px',
                height: '280px',
                zIndex: 30,
                pointerEvents: 'none',
              }}
            >
              {/* Rounded Google Lens Corner Brackets */}
              <div style={{ position: 'absolute', top: 0, left: 0, width: '40px', height: '40px', borderTop: '4px solid #ffffff', borderLeft: '4px solid #ffffff', borderTopLeftRadius: '1.25rem' }} />
              <div style={{ position: 'absolute', top: 0, right: 0, width: '40px', height: '40px', borderTop: '4px solid #ffffff', borderRight: '4px solid #ffffff', borderTopRightRadius: '1.25rem' }} />
              <div style={{ position: 'absolute', bottom: 0, left: 0, width: '40px', height: '40px', borderBottom: '4px solid #ffffff', borderLeft: '4px solid #ffffff', borderBottomLeftRadius: '1.25rem' }} />
              <div style={{ position: 'absolute', bottom: 0, right: 0, width: '40px', height: '40px', borderBottom: '4px solid #ffffff', borderRight: '4px solid #ffffff', borderBottomRightRadius: '1.25rem' }} />

              {/* Glowing Laser Scan Line Animation */}
              <div className="google-lens-scan-line" />
            </div>

            {/* Bottom Google Lens Action & Shutter Controls Overlay */}
            <div
              style={{
                position: 'relative',
                zIndex: 50,
                width: '100%',
                padding: '1.5rem 1rem 2rem 1rem',
                background: 'linear-gradient(to top, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0) 100%)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '1rem',
                boxSizing: 'border-box',
              }}
            >
              <p style={{ fontSize: '0.875rem', color: '#e2e8f0', margin: 0, textShadow: '0 1px 3px rgba(0,0,0,0.8)' }}>
                Đưa camera đến mã QR hoặc tải ảnh từ thư viện
              </p>

              {/* Action Buttons Toolbar (Upload image or Stop Camera) */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', justifyContent: 'center' }}>
                <label
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.625rem 1.25rem',
                    borderRadius: '9999px',
                    backgroundColor: 'rgba(255, 255, 255, 0.25)',
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)',
                    color: '#ffffff',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    cursor: 'pointer',
                    border: '1px solid rgba(255, 255, 255, 0.35)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                  }}
                >
                  <ImageIcon size={18} />
                  <span>Tải ảnh QR từ thư viện</span>
                  <input type="file" accept="image/*" onChange={handleFileUpload} style={{ display: 'none' }} />
                </label>

                <button
                  onClick={() => {
                    stopCameraScanner();
                    setRestoreMode('text');
                  }}
                  style={{
                    padding: '0.625rem 1.25rem',
                    borderRadius: '9999px',
                    backgroundColor: '#fee2e2',
                    color: '#e43b12',
                    border: 'none',
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.375rem',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                  }}
                >
                  <X size={16} />
                  <span>Tắt Camera</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Mode 3: Upload QR File */}
        {restoreMode === 'upload' && (
          <div>
            <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 600, color: '#333', marginBottom: '0.375rem' }}>
              Chọn file ảnh chứa mã QR khôi phục:
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={handleFileUpload}
              disabled={isScanningFile}
              style={{
                width: '100%',
                padding: '0.5rem',
                borderRadius: '0.375rem',
                border: '1px solid var(--border-light)',
                backgroundColor: '#f8fafc',
                fontSize: '0.875rem',
              }}
            />
            {isScanningFile && (
              <p style={{ fontSize: '0.8125rem', color: 'var(--action-primary)', marginTop: '0.5rem' }}>
                Đang quét file ảnh QR, vui lòng chờ...
              </p>
            )}
            <div id="qr-file-dummy-reader" style={{ display: 'none' }} />
          </div>
        )}
      </div>

      {/* Owned Collections Section */}
      <h3 style={{ fontSize: '1.125rem', fontWeight: 700, marginBottom: '0.75rem', color: 'var(--text-main)' }}>
        Bộ sưu tập của bạn ({ownedCollections.length})
      </h3>

      {loadingOwned ? (
        <div style={{ padding: '1rem', textAlign: 'center', color: '#666' }}>Đang kiểm tra...</div>
      ) : ownedCollections.length === 0 ? (
        <div
          style={{
            backgroundColor: '#ffffff',
            padding: '1.25rem',
            borderRadius: '0.5rem',
            textAlign: 'center',
            color: '#666',
            border: '1px solid var(--border-light)',
            fontSize: '0.875rem',
          }}
        >
          Chưa có bộ sưu tập nào liên kết với mã này.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {ownedCollections.map((col) => (
            <div
              key={col.id}
              style={{
                backgroundColor: '#ffffff',
                borderRadius: '0.5rem',
                border: '1px solid var(--border-light)',
                padding: '1rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '0.5rem',
              }}
            >
              <div>
                <div style={{ fontSize: '0.9375rem', fontWeight: 700, color: 'var(--text-main)' }}>{col.name}</div>
                <div style={{ fontSize: '0.8125rem', color: '#666', marginTop: '0.125rem' }}>
                  Tác giả: <strong>{col.author}</strong> | Số file: {col.files?.length || 0}
                </div>
              </div>

              <button
                onClick={() => navigate(`/collections/${col.id}`)}
                style={{
                  padding: '0.375rem 0.75rem',
                  borderRadius: '0.375rem',
                  backgroundColor: 'var(--accent-highlight)',
                  border: 'none',
                  color: 'var(--text-on-light)',
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Quản lý
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
